import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { cn } from '../lib/utils';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  Edit,
  ShoppingCart,
  FileText,
  Loader2,
  X,
  AlertTriangle,
  Split,
} from 'lucide-react';
import Layout from '../components/Layout';
import FileAttachmentManager from '../components/FileAttachmentManager';
import ProductTable from '../components/ProductTable';
import BulkDownloadProductAttachmentsButton from '../components/BulkDownloadProductAttachmentsButton';
import PartialConversionDialog from '../components/PartialConversionDialog';
import quotationService from '../services/quotationService';
import authService from '../services/authService';
import excelExportService from '../services/excelExportService';
import TemplateSelector from '../components/TemplateSelector';

const QuotationDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [quotation, setQuotation] = useState(location.state?.quotationData || null);
  const [loading, setLoading] = useState(!location.state?.quotationData);
  const [error, setError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: '', quotation: null });
  const [templateSelectorOpen, setTemplateSelectorOpen] = useState(false);
  const [pendingExportQuotation, setPendingExportQuotation] = useState(null);
  const [partialConversionOpen, setPartialConversionOpen] = useState(false);



  const loadQuotation = useCallback(async () => {
    if (!id || quotation) return; // Skip if we already have quotation data

    try {
      setLoading(true);
      setError('');

      const response = await quotationService.getQuotation(id);
      setQuotation(response);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load quotation details');
    } finally {
      setLoading(false);
    }
  }, [id, quotation]);

  useEffect(() => {
    loadQuotation();
  }, [loadQuotation]);






  const handleAction = (action) => {
    if (action === 'edit') {
      navigate(`/quotations/${id}/edit`);
    } else if (action === 'convert') {
      setConfirmDialog({ open: true, action, quotation });
    } else if (action === 'export') {
      // Open template selector instead of legacy confirm
      setPendingExportQuotation(quotation);
      setTemplateSelectorOpen(true);
    }
  };

  const handleConfirmAction = async () => {
    const { action, quotation } = confirmDialog;
    setConfirmDialog({ open: false, action: '', quotation: null });

    try {
      if (action === 'convert') {
        const response = await quotationService.convertQuotationToOrder(quotation._id);
        // Note: The original quotation is deleted after conversion
        alert('Quotation converted to sales order successfully! The original quotation has been removed.');

        // Navigate to the sales order page
        if (response.order && response.order._id) {
          navigate(`/sales/${response.order._id}/view`, {
            state: { orderData: response.order }
          });
        } else {
          // Fallback: navigate to sales list if order ID is not available
          navigate('/sales');
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${action} quotation`);
    }
  };

  const handleTemplateSelected = async (template) => {
    setTemplateSelectorOpen(false);
    try {
      const qt = pendingExportQuotation || quotation;
      const templateId = template?.template_id;
      if (!templateId) {
        setError('未选择模板');
        return;
      }
      const { blob, filename } = await excelExportService.exportUsingTemplate('quotation', {
        template_id: templateId,
        quotation_ids: [qt._id],
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `quotation_${qt.quotation_id || qt._id}.xlsx`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      setError('');
    } catch (err) {
      console.error('Template export error:', err);
      setError(err?.message || '导出失败');
    } finally {
      setPendingExportQuotation(null);
    }
  };

  const handleTemplateSelectorClose = () => {
    setTemplateSelectorOpen(false);
    setPendingExportQuotation(null);
  };

  const handlePartialConversionComplete = (response) => {
    // Refresh quotation data to show updated conversion status
    setQuotation(null); // Clear to force reload
    loadQuotation();

    // Navigate to the new sales order
    if (response.order && response.order._id) {
      navigate(`/sales/${response.order._id}/view`, {
        state: { orderData: response.order }
      });
    }
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'bg-warning/10 text-warning border-warning/30';
      case 'quoted': return 'bg-info/10 text-info border-info/30';
      case 'confirmed': return 'bg-success/10 text-success border-success/30';
      case 'completed': return 'bg-primary/10 text-primary border-primary/30';
      case 'converted': return 'bg-primary/10 text-primary border-primary/30';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusLabel = (status) => {
    const value = (status || '').toLowerCase();
    switch (value) {
      case 'draft':
        return '草稿';
      case 'pending':
        return '待处理';
      case 'quoted':
        return '已报价';
      case 'confirmed':
        return '已确认';
      case 'completed':
      case 'converted':
        return '已转换';
      default:
        return status || '待处理';
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Derive display totals consistent with backend semantics
  const pricingMode = quotation?.pricing_mode || 'tax_exclusive';
  const taxRate = typeof quotation?.tax_rate === 'number' ? quotation.tax_rate : 0.13;
  const subtotalExcl = typeof quotation?.subtotal_tax_exclusive === 'number' ? quotation.subtotal_tax_exclusive : undefined;
  const subtotalIncl = typeof quotation?.subtotal_tax_inclusive === 'number' ? quotation.subtotal_tax_inclusive : undefined;
  const taxAmountResp = typeof quotation?.tax_amount === 'number' ? quotation.tax_amount : undefined;
  const subtotalDisplay = typeof quotation?.subtotal === 'number'
    ? quotation.subtotal
    : (pricingMode === 'tax_inclusive' ? (subtotalIncl ?? 0) : (subtotalExcl ?? 0));
  const taxAmountDisplay = typeof taxAmountResp === 'number'
    ? taxAmountResp
    : (typeof subtotalIncl === 'number' && typeof subtotalExcl === 'number'
        ? (subtotalIncl - subtotalExcl)
        : (subtotalDisplay * taxRate));
  const totalInclDisplay = typeof quotation?.total_with_tax === 'number'
    ? quotation.total_with_tax
    : (pricingMode === 'tax_inclusive'
        ? subtotalDisplay
        : (subtotalDisplay + taxAmountDisplay));

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Layout title="报价详情" activeTab="quotations">
        <div className="max-w-7xl mx-auto py-6 px-4">
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!quotation) {
    return (
      <Layout title="报价详情" activeTab="quotations">
        <div className="max-w-7xl mx-auto py-6 px-4">
          <div className="text-center py-16">
            <h2 className="text-lg font-semibold text-muted-foreground">
              未找到报价单
            </h2>
            <Button
              onClick={() => navigate('/quotations')}
              className="mt-4"
            >
              返回报价单列表
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <>
    <Layout title="报价详情" activeTab="quotations">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription className="flex items-center justify-between">
              <span>{error}</span>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setError('')}>
                <X className="h-4 w-4" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/quotations')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              报价单详情
            </h1>
            <p className="text-muted-foreground">
              报价单：{quotation.quotation_id}
            </p>
          </div>
        </div>

        {/* Quotation Information */}
        <Card className="border bg-card mb-6">
          <CardContent className="pt-6">
            <h2 className="text-lg font-semibold mb-4">报价单信息</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-muted-foreground">报价单编号</p>
                <p className="font-medium">{quotation.quotation_id}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">客户名称</p>
                <p className="font-medium">{quotation.client_name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">状态</p>
                <Badge className={cn('border', getStatusClass(quotation.status))}>
                  {getStatusLabel(quotation.status)}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">价格列</p>
                <p className="font-medium">{quotation.price_column || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">产品数量</p>
                <p className="font-medium">{quotation.total_products || quotation.products?.length || 0}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">小计</p>
                {authService.canViewQuotationPrices() ? (
                  <p className="font-bold">{formatCurrency(subtotalDisplay)}</p>
                ) : (
                  <p className="font-bold text-muted-foreground">*** 无权查看 ***</p>
                )}
              </div>

              <div>
                <p className="text-sm text-muted-foreground">税额（{Math.round((quotation?.tax_rate || 0.13) * 100)}%）</p>
                {authService.canViewQuotationPrices() ? (
                  <p className="font-medium">{formatCurrency(taxAmountDisplay)}</p>
                ) : (
                  <p className="font-medium text-muted-foreground">*** 无权查看 ***</p>
                )}
              </div>
              <div>
                <p className="text-sm text-muted-foreground">合计（含税）</p>
                {authService.canViewQuotationPrices() ? (
                  <p className="text-lg font-bold text-primary">{formatCurrency(totalInclDisplay)}</p>
                ) : (
                  <p className="text-lg font-bold text-muted-foreground">*** 无权查看 ***</p>
                )}
              </div>
              <div>
                <p className="text-sm text-muted-foreground">日期</p>
                <p className="font-medium">{formatDate(quotation.date)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">创建日期</p>
                <p className="font-medium">{formatDate(quotation.created_at)}</p>
              </div>
              {quotation.comment && (
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground">备注</p>
                  <p>{quotation.comment}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Products Table */}
        <Card className="border bg-card mb-6">
          <CardContent className="pt-6">
            <ProductTable
              products={quotation.products || []}
              view="quotation"
              readOnly={true}
              title={`产品清单 (${quotation.products?.length || 0} 项)`}
            />
          </CardContent>
        </Card>

        {/* Price Summary */}
        <Card className="border bg-card mb-6">
          <CardContent className="pt-6">
            <h2 className="text-lg font-semibold mb-4">价格汇总</h2>
            <div className="flex justify-end">
              <div className="text-right min-w-[200px] space-y-2">
                <div className="flex justify-between">
                  <span>小计：</span>
                  {authService.canViewQuotationPrices() ? (
                    <span className="font-medium">{formatCurrency(subtotalDisplay)}</span>
                  ) : (
                    <span className="font-medium text-muted-foreground">*** 无权查看 ***</span>
                  )}
                </div>
                <div className="flex justify-between">
                  <span>税额（{Math.round((quotation?.tax_rate || 0.13) * 100)}%）：</span>
                  {authService.canViewQuotationPrices() ? (
                    <span className="font-medium">{formatCurrency(taxAmountDisplay)}</span>
                  ) : (
                    <span className="font-medium text-muted-foreground">*** 无权查看 ***</span>
                  )}
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-lg font-bold">合计：</span>
                  {authService.canViewQuotationPrices() ? (
                    <span className="text-lg font-bold text-primary">{formatCurrency(totalInclDisplay)}</span>
                  ) : (
                    <span className="text-lg font-bold text-muted-foreground">*** 无权查看 ***</span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* File Attachments */}
        <FileAttachmentManager
          entityType="quotations"
          entityId={quotation?._id}
          title="报价单附件"
          className="mb-6"
        />

        {/* Action Buttons */}
        <div className="flex justify-end gap-2 mt-6">
          <Button
            variant="outline"
            onClick={() => navigate('/quotations')}
          >
            返回报价单列表
          </Button>
          <Button
            variant="outline"
            onClick={() => handleAction('export')}
          >
            <FileText className="h-4 w-4 mr-2" />
            导出
          </Button>
          <BulkDownloadProductAttachmentsButton
            entityType="quotations"
            entityId={quotation._id}
            entityName={quotation.quotation_id}
          />
          <Button
            variant="outline"
            onClick={() => handleAction('edit')}
          >
            <Edit className="h-4 w-4 mr-2" />
            编辑报价单
          </Button>
          <Button
            variant="outline"
            onClick={() => setPartialConversionOpen(true)}
          >
            <Split className="h-4 w-4 mr-2" />
            部分转换
          </Button>
          <Button
            onClick={() => handleAction('convert')}
            disabled={quotation.status === 'completed' || quotation.status === 'converted'}
          >
            <ShoppingCart className="h-4 w-4 mr-2" />
            {quotation.status === 'completed' || quotation.status === 'converted'
              ? '已转换'
              : '全部转换'}
          </Button>
        </div>

        {/* Confirmation Dialog (convert only) */}
        <Dialog
          open={confirmDialog.open}
          onOpenChange={(open) => !open && setConfirmDialog({ open: false, action: '', quotation: null })}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                确认{confirmDialog.action === 'convert' ? '转换为合同' : ''}
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-muted-foreground">
                {confirmDialog.action === 'convert' && `确认要将报价单 ${confirmDialog.quotation?.quotation_id} 转换为销售合同吗？`}
              </p>
              {confirmDialog.action === 'convert' && (
                <div className="mt-4 p-3 bg-warning/10 border border-warning/30 rounded-md flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-warning mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-warning font-medium">
                    注意：转换成功后，原报价单将被删除。所有数据将保留在新创建的销售合同中。
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setConfirmDialog({ open: false, action: '', quotation: null })}
              >
                取消
              </Button>
              <Button onClick={handleConfirmAction}>
                确认
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
    <TemplateSelector
      open={templateSelectorOpen}
      onClose={handleTemplateSelectorClose}
      onSelect={handleTemplateSelected}
      entityType="quotation"
      title="选择导出模板"
      allowManagement={true}
    />
    <PartialConversionDialog
      open={partialConversionOpen}
      onClose={() => setPartialConversionOpen(false)}
      quotation={quotation}
      onConversionComplete={handlePartialConversionComplete}
    />
    </>
  );
};

export default QuotationDetailPage;
